import { cn } from '@/lib/utils';
// Custom auth replaces Cognito. `AuthUser` is kept as a local type
// alias for components that previously took an `AuthUser` prop —
// they only ever read `.userId` / `.username`, both stand-ins for
// "are we logged in".
import { Maintenance } from '@chiroup/components/Maintenance';
import { ConsentMethod } from '@chiroup/core/enums/ConsentMethod.enum';
import { SmsConsentStatus } from '@chiroup/core/enums/SmsConsentStatus.enum';
import { hexToRgb } from '@chiroup/core/functions/hexToRgb';
import { Menu, Transition } from '@headlessui/react';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useQueryClient } from 'react-query';
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useNavigate,
  useLocation as useRouterLocation,
} from 'react-router-dom';
import healthcomLogo from '../assets/healthcom-logo.png';
import { hcVerbiage, Language } from '../common/verbiage';
import {
  CLINIC_COLORS_STORAGE_KEY,
  HEALTHCOM_ACCENT,
  HEALTHCOM_PRIMARY,
  SELECTED_CLINIC_ID_STORAGE_KEY,
} from '../constants/brand';
import { PatientClinic, PatientInfo, PatientPortalContext } from '../contexts/PatientPortalContext';
import useLocation from '../hooks/useLocation';
import { fetchMeClinicsCached, ME_CLINICS_QUERY_KEY } from '../hooks/useMeClinics';
import { useTheme } from '../hooks/useTheme';
import { authService } from '../services/auth.service';
import healthcomService from '../services/healthcom.service';
import { portalSession } from '../services/portalSession';
import userService from '../services/user.service';
import Login from './Login';
import { NotFoundSchedule } from './NotFoundSchedule';
import Schedule from './Schedule';
import ThankYou from './ThankYou';
import Appointments from './appointments/Appointments';
import { useLoadingOverlay, useLoadingOverlayPhase } from './common/LoadingOverlay';
import MainContentSkeleton from './common/MainContentSkeleton';
import ScrollToTop from './common/ScrollToTop';
import AdviceDetail from './portal/AdviceDetail';
import Advices from './portal/Advices';
import BalancePlaceholder from './portal/BalancePlaceholder';
import BottomNav from './portal/BottomNav';
import ClinicInfo from './portal/ClinicInfo';
import ClinicSelect from './portal/ClinicSelect';
import ConditionDetail from './portal/ConditionDetail';
import Conditions from './portal/Conditions';
import ConsentRunner from './portal/ConsentRunner';
import ExerciseDetail from './portal/ExerciseDetail';
import Exercises from './portal/Exercises';
import HealthHub from './portal/HealthHub';
import Home from './portal/Home';
import PatientSelect from './portal/PatientSelect';
import RequestFormRunner from './portal/RequestFormRunner';
import SideNav from './portal/SideNav';
import SurveyRunner from './portal/SurveyRunner';
import UploadRunner from './portal/UploadRunner';

type AuthUser = { userId: string; username: string };

const getCurrentUser = async (): Promise<AuthUser> => {
  const patient = portalSession.getPatient();

  if (!portalSession.isLikelyValid() || !patient) {
    throw new Error('Not authenticated');
  }

  return { userId: patient.patientId, username: patient.patientId };
};

const signOut = async () => {
  await authService.logout();
};

export type ScheduleForm = {
  treatment: number | null;
  provider: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  time: string | null;
  patient: string | null;
  fname?: string | null;
  lname?: string | null;
  email?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  creditCardConsent?: boolean;
  smsConsent?: SmsConsentStatus | null;
  smsConsentMethod?: ConsentMethod | null;
};

const primaryColorDefault = HEALTHCOM_PRIMARY;

// TEMP: log whatever colors the API actually returns so we can verify the
// per-clinic colors are flowing through from backend → state → CSS vars.
// Remove once we're confident the pipeline is correct.
const logClinicColors = (source: string, primaryColor?: string, accentColor?: string) => {
  console.log(`[colors:${source}]`, { primaryColor, accentColor });
};

// Apply a primary/accent color pair to the :root CSS variables used by
// Tailwind utilities (`bg-primary`, `bg-accent`) and the
// `border-with-opacity` / `bg-with-opacity` helpers. Setting both the full
// `rgb()` and the CSV `*-base` form keeps both consumers happy.
const applyClinicColors = (primaryHex: string, accentHex?: string) => {
  const root = document.documentElement;

  try {
    const primaryCsv = hexToRgb(primaryHex);
    root.style.setProperty('--primary-color-base', primaryCsv);
    root.style.setProperty('--primary-color', `rgb(${primaryCsv})`);
  } catch {
    /* invalid hex — leave the var alone */
  }

  try {
    const accentCsv = hexToRgb(accentHex || HEALTHCOM_ACCENT);
    root.style.setProperty('--accent-color-base', accentCsv);
    root.style.setProperty('--accent-color', `rgb(${accentCsv})`);
  } catch {
    /* invalid hex — leave the var alone */
  }
};

// Persist the active clinic's colors so the boot script in index.html can
// restore them on refresh, avoiding a flash of HealthCom green.
const persistClinicColors = (primaryHex?: string, accentHex?: string) => {
  try {
    if (!primaryHex) {
      localStorage.removeItem(CLINIC_COLORS_STORAGE_KEY);

      return;
    }

    localStorage.setItem(
      CLINIC_COLORS_STORAGE_KEY,
      JSON.stringify({ primaryHex, accentHex: accentHex || HEALTHCOM_ACCENT })
    );
  } catch {
    /* localStorage blocked — not critical, we just lose the optimisation */
  }
};

// Persist the selected clinicId so the user lands in the same clinic on
// refresh without relying on URL paths. The authed portal no longer uses
// a path to identify the active clinic — localStorage is the source of
// truth, and "Switch Clinic" just updates this value instead of navigating.
const persistSelectedClinicId = (clinicId?: number) => {
  try {
    if (typeof clinicId !== 'number') {
      localStorage.removeItem(SELECTED_CLINIC_ID_STORAGE_KEY);

      return;
    }

    localStorage.setItem(SELECTED_CLINIC_ID_STORAGE_KEY, String(clinicId));
  } catch {
    /* localStorage blocked — fall through to URL-path behaviour */
  }
};

const readSelectedClinicId = (): number | undefined => {
  try {
    const raw = localStorage.getItem(SELECTED_CLINIC_ID_STORAGE_KEY);

    if (!raw) return undefined;
    const parsed = Number(raw);

    return Number.isFinite(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const scheduleFormDefaults: ScheduleForm = {
  treatment: null,
  provider: null,
  date: null,
  startTime: null,
  endTime: null,
  time: null,
  patient: null,
  fname: null,
  lname: null,
  email: null,
  phone: null,
  dateOfBirth: null,
  creditCardConsent: false,
  smsConsent: null,
  smsConsentMethod: null,
};

type ContainerProps = {
  // Called when the user signs out from inside the portal. Lets the
  // parent (RootLanding) reset its own auth state machine back to the
  // login screen instead of keeping Container mounted with user=null.
  onSignedOut?: () => void;
};

const Container = ({ onSignedOut }: ContainerProps) => {
  const { resolved: themeResolved, toggle: toggleTheme } = useTheme();
  const [user, setUser] = useState<AuthUser | null>(null);
  // Tracks whether the initial `getCurrentUser()` call has resolved. Until
  // this is true we don't know if the user is logged in, so we show a full
  // LoadingPage instead of flashing "Log in" then immediately swapping to
  // the user menu once the Cognito session loads.
  const [authChecked, setAuthChecked] = useState(false);
  const [downForMaintenance, setDownForMaintenance] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const queryClient = useQueryClient();
  const [me, setMe] = useState<{
    patients: any[];
  } | null>(null);
  const [values, setValues] = useState<ScheduleForm>(scheduleFormDefaults);

  // `values` lives here (above Schedule) because Login pre-fills
  // phone/email/DOB into the same store before the auth handoff. That
  // means it survives Schedule unmount, so without an explicit reset
  // a half-finished booking shows back up the next time the user
  // opens the schedule (e.g., after switching patient from the user
  // menu). Reset whenever the user navigates away from /schedule;
  // route-change driven so React Strict Mode's dev-only double-mount
  // doesn't wipe the form on the initial visit. Login's path is
  // /login, so the pre-fill flow is unaffected.
  const previousRoutePathRef = useRef(routerLocation.pathname);
  useEffect(() => {
    const previousPath = previousRoutePathRef.current;
    previousRoutePathRef.current = routerLocation.pathname;

    if (previousPath === '/schedule' && routerLocation.pathname !== '/schedule') {
      setValues(scheduleFormDefaults);
    }
  }, [routerLocation.pathname]);

  // Patient portal state
  const [portalClinics, setPortalClinics] = useState<PatientClinic[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<PatientClinic | null>(null);
  const [portalPatients, setPortalPatients] = useState<PatientInfo[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientInfo | null>(null);
  // Start as `true` so the loader stays engaged from mount through the
  // first fetchPortalData call. Without this, there's a one-render
  // window after the auth-check effect (user set, authChecked=true)
  // but before the user-change effect kicks off fetchPortalData where
  // portalLoading is still false — which falls through to <Home>
  // briefly with no selectedPatient. Same window can flash the
  // clinic/patient picker before auto-selection completes. See
  // logout/auto-select bug fixes.
  const [portalLoading, setPortalLoading] = useState(true);
  // Tracks whether the initial portal fetch has finished at least
  // once. We use this to gate the full-viewport `HealthcomLoadingPage`
  // during first load: without it, the chrome (header + sidebar)
  // would paint with default state the moment `isFetching` flipped
  // false, while `portalLoading` was still in flight — producing a
  // visible "glitch" as patient/location data streamed in
  // asynchronously. Subsequent fetches (clinic switching, patient
  // refresh) intentionally do NOT re-engage the spinner; those keep
  // the chrome up and surface a `MainContentSkeleton` in the main
  // column so the user has a stable reference frame as they switch
  // contexts.
  const [initialPortalLoadDone, setInitialPortalLoadDone] = useState(false);

  // Feed the clinic's vanity path into useLocation so it can fetch
  // /location/{path} and surface disciplines/providers to Schedule. The
  // portal state above is the source of truth; fall back to the patient's
  // clinic path if the meClinics summary got overwritten by mePortal.
  const activeClinicPath = selectedClinic?.path || selectedPatient?.clinic?.path;
  // Clinics without a vanity URL have no path — fall back to clinicId so
  // location + appointments still resolve in the authed portal.
  const activeClinicId = selectedClinic?.clinicId ?? selectedPatient?.clinic?.clinicId ?? null;
  const {
    data: clinicLocationData,
    path,
    isFetching,
  } = useLocation(activeClinicPath, activeClinicId);
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    if (clinicLocationData?.googleAnalyticsTrackingId) {
      ReactGA.initialize([
        {
          trackingId: clinicLocationData.googleAnalyticsTrackingId,
        },
      ]);
    }
  }, [clinicLocationData]);

  // `getMe` is the single entrypoint for `/me/{path}`. Schedule calls it
  // on mount; Login calls it after sign-in. The fetch should run AT MOST
  // ONCE per Container lifetime (per path) — without this gate, any
  // upstream remount churn (e.g., Schedule unmounting/remounting because
  // portalLoading flipped) would each trigger a fresh /me/{path} hit
  // and potentially loop. Container is the right place for the gate
  // because Container's lifetime spans the whole authed session, so
  // the ref is stable across child remounts.
  const meFetchInFlightRef = useRef<Promise<any> | null>(null);
  const meFetchedForPathRef = useRef<string | null>(null);

  const getMe = useMemo(() => {
    return async (force = false) => {
      // Already fetched for this path? Reuse the existing me state.
      // `force` is the sign-in escape hatch: the gate is also set when a
      // pre-auth fetch 401s, so without bypassing it the post-OTP refetch
      // would no-op and `me` would never populate (login screen stays put
      // until a manual refresh). Sign-in callers pass `force: true`.
      if (!force && meFetchedForPathRef.current === path) return undefined;
      // Fetch in flight? Wait on it instead of starting another.
      if (meFetchInFlightRef.current) return meFetchInFlightRef.current;

      const promise = (async () => {
        try {
          const meRes = await userService.me(path);
          meFetchedForPathRef.current = path || null;
          setMe(meRes);

          return meRes;
        } catch (err: any) {
          if (err?.response?.data?.statusCode === 503) {
            const root = document.documentElement;
            root.style.setProperty('--primary-color-base', hexToRgb('#000000'));
            root.style.setProperty('--primary-color', `rgba(${hexToRgb('#000000')})`);
            setDownForMaintenance(err?.response?.data);
          }
          // Mark as fetched so we don't loop on persistent errors. The
          // user can navigate away and back to retry — Container will
          // remount and the ref will reset with it.
          meFetchedForPathRef.current = path || null;

          return undefined;
        } finally {
          meFetchInFlightRef.current = null;
        }
      })();

      meFetchInFlightRef.current = promise;

      return promise;
    };
  }, [path]);

  /**
   * Fetch patient portal data from the HealthCom /me endpoint.
   * Handles:
   * - Multiple clinics (phone/email shared across clinics) — fetched in
   *   parallel from /me/clinics so the user menu can offer "Switch Clinic"
   * - Multiple patients per clinic (family accounts)
   * - Single clinic + single patient (auto-select)
   *
   * If `clinicId` is supplied the clinicId-based backend variant runs,
   * which doesn't require the clinic to have a vanity URL path. Otherwise
   * falls back to the path-based endpoint (initial load / scheduling-site
   * flow).
   */
  const fetchPortalData = useCallback(
    async (clinicIdArg?: number, opts?: { silent?: boolean }) => {
      if (!user) return;

      // Silent refreshes (e.g. after an in-page action like paying a
      // balance) skip `portalLoading` entirely — flipping it swaps the
      // routes for <MainContentSkeleton>, which unmounts the current
      // page and wipes its local state (like the payment-success card).
      const silent = !!opts?.silent;

      if (!silent) setPortalLoading(true);

      try {
        // Always load the clinics list first so the switcher + header
        // show up even if the portal fetch fails. Previously these were
        // tied together in one Promise.all — one failure = entirely blank
        // app. Split so they fail independently.
        const clinicsRes = await fetchMeClinicsCached(queryClient).catch((err: unknown) => {
          console.warn('Failed to load clinics list for switcher:', err);

          return null;
        });

        const allClinics: PatientClinic[] = clinicsRes?.clinics || [];
        console.log('[colors:meClinics:raw]', allClinics);

        // Resolve the active clinic from (in order): explicit arg,
        // localStorage, first clinic with a path, first clinic in the
        // list. This gives us something to render even when other
        // signals are missing.
        const storedClinicId = readSelectedClinicId();
        const activeClinic: PatientClinic | undefined =
          (clinicIdArg && allClinics.find(c => c.clinicId === clinicIdArg)) ||
          (storedClinicId && allClinics.find(c => c.clinicId === storedClinicId)) ||
          allClinics.find(c => !!c.path) ||
          allClinics[0];

        if (allClinics.length) {
          setPortalClinics(allClinics);
        }

        if (activeClinic) {
          setSelectedClinic(activeClinic);

          if (activeClinic.clinicId) {
            persistSelectedClinicId(activeClinic.clinicId);
          }
        }

        // Fetch portal data for the active clinic. Prefer the
        // path-based endpoint — it's the one that's been in prod forever
        // and is guaranteed to be live. The clinicId-based endpoint is
        // newer; fall back to it only when the clinic has no vanity URL
        // path. After the meClinics SQL fallback lands every clinic
        // should come back with a path anyway, so this fallback is rare.
        let portalRes: any = null;

        if (activeClinic?.path) {
          try {
            portalRes = await healthcomService.mePortal(activeClinic.path);
          } catch (err) {
            console.warn('[portal] mePortal(path) failed, trying mePortalByClinicId:', err);

            if (activeClinic.clinicId) {
              try {
                portalRes = await healthcomService.mePortalByClinicId(activeClinic.clinicId);
              } catch (fallbackErr) {
                console.error('[portal] clinicId fallback also failed:', fallbackErr);
              }
            }
          }
        } else if (activeClinic?.clinicId) {
          try {
            portalRes = await healthcomService.mePortalByClinicId(activeClinic.clinicId);
          } catch (err) {
            console.error('[portal] clinic has no path and mePortalByClinicId failed:', err);
          }
        } else if (path) {
          // No clinic context at all yet — use the URL path if present
          // (legacy scheduling-site entry point).
          portalRes = await healthcomService.mePortal(path);
        }

        console.log('[colors:mePortal:raw]', {
          patientCount: portalRes?.patients?.length,
          firstClinic: portalRes?.patients?.[0]?.clinic,
          firstPatient: portalRes?.patients?.[0]
            ? {
                ID: portalRes.patients[0].ID,
                name: `${portalRes.patients[0].fname} ${portalRes.patients[0].lname}`,
                exerciseCount: portalRes.patients[0].exercises?.length ?? 0,
                conditionCount: portalRes.patients[0].conditions?.length ?? 0,
                adviceCount: portalRes.patients[0].advice?.length ?? 0,
                balances: portalRes.patients[0].balances,
              }
            : null,
        });

        const patients: PatientInfo[] = portalRes?.patients || [];

        // Mirror the clinic-level split-balance-by-location flag onto each
        // patient so the Balance page (which works off selectedPatient) can
        // switch to the per-location view without threading extra state.
        const splitBalanceByLocation = !!portalRes?.splitBalanceByLocation;
        patients.forEach(p => {
          p.splitBalanceByLocation = splitBalanceByLocation;
        });

        if (!patients.length) {
          // No patients returned — still leave the clinics list and
          // selectedClinic populated so the switcher works. The user can
          // pick a different clinic if the current one isn't a match.
          console.warn('[portal] No patients returned for active clinic');

          if (!silent) setPortalLoading(false);

          return;
        }

        // If mePortal returned a more complete clinic object (with
        // logo/colors from the location row), prefer that over the
        // meClinics summary — but keep the `path` from meClinics because
        // mePortal's clinic payload doesn't include it, and useLocation
        // needs it to fetch disciplines/providers for the schedule flow.
        const enrichedClinic = patients[0]?.clinic;

        if (enrichedClinic && enrichedClinic.clinicId === activeClinic?.clinicId) {
          setSelectedClinic({
            ...enrichedClinic,
            path: enrichedClinic.path || activeClinic?.path,
          });

          if (activeClinic?.path) {
            // Mirror the path onto every patient's clinic so Home /
            // Schedule / useLocation can read it off selectedPatient.
            // mePortal can return `path: null` for some patients in the
            // same household even when others have it, so backfill any
            // that are missing it — not just the case where patients[0]
            // is also missing it.
            patients.forEach(p => {
              if (p.clinic && !p.clinic.path) {
                p.clinic = { ...p.clinic, path: activeClinic.path };
              }
            });
          }
        }

        if (patients.length === 1) {
          setPortalPatients(patients);
          setSelectedPatient(patients[0]);
          sessionStorage.setItem('selectedPatientId', patients[0].ID);

          if (patients[0].language) {
            setLanguage(patients[0].language as Language);
          }
        } else {
          setPortalPatients(patients);
          const savedPatientId = sessionStorage.getItem('selectedPatientId');
          const savedPatient = savedPatientId ? patients.find(p => p.ID === savedPatientId) : null;

          if (savedPatient) {
            setSelectedPatient(savedPatient);

            if (savedPatient.language) {
              setLanguage(savedPatient.language as Language);
            }
          } else {
            setSelectedPatient(null);
          }
        }
      } catch (err) {
        console.error('Failed to fetch portal data:', err);
      }

      if (!silent) setPortalLoading(false);
    },
    [user, path, queryClient]
  );

  useEffect(() => {
    let isDestroyed = false;

    const doIt = async () => {
      try {
        const currentUser = await getCurrentUser();

        if (!isDestroyed && currentUser) {
          setUser(currentUser as any);
          // Intentionally NOT calling getMe() here. `/me/{path}` returns the
          // payment-method / SMS-consent / phone fields that only the Schedule
          // flow needs, and most portal sessions never open it. Schedule
          // triggers getMe() lazily on mount instead — saves one API call on
          // every Home/HealthHub/Exercises pageload.
        }
      } catch {
        await signOut();

        if (!isDestroyed) {
          setUser(null);
          setMe(null);
          setValues(scheduleFormDefaults);
          // Tell RootLanding the session is no longer valid so it
          // swaps Container out for the login screen. Without this
          // we'd stay mounted with `user=null` and never recover.
          onSignedOut?.();
        }
      } finally {
        if (!isDestroyed) {
          setAuthChecked(true);
        }
      }
    };

    // Always run the auth check — previously this was gated on `path`
    // being present (the old /:path/* route wouldn't mount Container
    // without a path). With the new path-less routing, URL is `/` on
    // mount and the auth check still needs to fire.
    //
    // Empty deps: this effect doesn't read `path` or `getMe`. Earlier
    // they were listed because `getMe` was called here, but it isn't
    // anymore. Keeping them caused the effect to re-fire whenever
    // `path` flipped (undefined → "main-1" once mePortal resolved),
    // which kicked off a second `setUser(currentUser)` with a fresh
    // Cognito reference and started a render loop downstream.
    doIt();

    return () => {
      isDestroyed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch portal data when the user is set. Intentionally only depends
  // on `user` — the previous `[user, fetchPortalData]` wired in a feedback
  // loop because `fetchPortalData` is `useCallback([user, path, …])`,
  // and `path` flips from `undefined` → "main-1" once the first
  // fetchPortalData call sets `selectedClinic`. Each flip recreated
  // fetchPortalData → re-fired this effect → kicked off another
  // fetchPortalData → setPortalLoading(true)/false → unmounted and
  // remounted Schedule → new getMe call → ~300 requests/sec.
  // Using a ref keeps the closure fresh without retriggering the effect.
  const fetchPortalDataRef = useRef(fetchPortalData);

  useEffect(() => {
    fetchPortalDataRef.current = fetchPortalData;
  }, [fetchPortalData]);

  useEffect(() => {
    if (user) {
      fetchPortalDataRef.current();
    }
  }, [user]);

  // Mark the initial portal load complete the first time portalLoading
  // drops to false post-auth. After this flip, subsequent refetches
  // (clinic/patient switch, manual refresh) won't re-engage the
  // full-viewport loader — they fall through to the chrome+skeleton
  // path below.
  useEffect(() => {
    if (initialPortalLoadDone) return;

    if (authChecked && user && !portalLoading) {
      setInitialPortalLoadDone(true);
    }
  }, [initialPortalLoadDone, authChecked, user, portalLoading]);

  // Loader visibility is handled by the app-level LoadingOverlay
  // provider (see common/LoadingOverlay.tsx). We register here so
  // the overlay stays engaged through the auth check, clinics
  // fetch, mePortal fetch, and location fetch — and crucially,
  // through the RootLanding → Container handoff. Remounting the
  // HealthcomLoadingPage at a different React position (which
  // would happen if RootLanding and Container each rendered their
  // own copy) restarts every CSS animation from 0% and reads as a
  // mid-load glitch. Lifting the loader to App level keeps a
  // single React instance mounted throughout.
  const isInitialLoading =
    isFetching || !authChecked || (!!user && portalLoading && !initialPortalLoadDone);

  useLoadingOverlay(isInitialLoading);
  const loaderPhase = useLoadingOverlayPhase();

  // Single source of truth for the :root color CSS vars. Listens to every
  // possible source (selectedPatient, selectedClinic, clinicLocationData)
  // so whichever updates last wins — avoids the earlier race where two
  // effects competed to set the vars.
  //
  // Priority (first that provides a primaryColor wins):
  //   1. selectedPatient.clinic — most authoritative once mePortal loads
  //   2. selectedClinic — set synchronously when the user picks from the
  //      switcher (covers clinics with no URL path)
  //   3. clinicLocationData — available before mePortal finishes; gives
  //      the first on-brand paint after navigation
  //
  // Two subtle cases the early-return guards handle:
  //   - Before auth has been checked, keep whatever the boot script
  //     restored from localStorage — don't flash HealthCom green.
  //   - Authenticated but portal data hasn't arrived yet: same — keep
  //     the boot-script color until we actually have new data to apply.
  //     The previous implementation fell through to HealthCom green in
  //     this window, causing the `green → red → red` flash on refresh.
  useEffect(() => {
    if (!authChecked) return;

    const source =
      (selectedPatient?.clinic?.primaryColor && {
        label: 'selectedPatient',
        primaryColor: selectedPatient.clinic.primaryColor,
        accentColor: selectedPatient.clinic.accentColor,
      }) ||
      (selectedClinic?.primaryColor && {
        label: 'selectedClinic',
        primaryColor: selectedClinic.primaryColor,
        accentColor: selectedClinic.accentColor,
      }) ||
      (clinicLocationData?.primaryColor && {
        label: 'clinicLocationData',
        primaryColor: clinicLocationData.primaryColor,
        accentColor: clinicLocationData.accentColor,
      }) ||
      null;

    if (source) {
      logClinicColors(`apply:${source.label}`, source.primaryColor, source.accentColor);
      applyClinicColors(source.primaryColor, source.accentColor);
      persistClinicColors(source.primaryColor, source.accentColor);

      return;
    }

    // No clinic context. Two valid cases:
    //   - Not authenticated (logged out or pre-auth) → HealthCom defaults
    //   - In the clinic picker after Switch Clinic (authed, clinics loaded
    //     but none selected) → HealthCom defaults so the picker reads as
    //     HealthCom-branded, not as the last clinic's theme.
    // Otherwise (authed, waiting for data), leave the boot-script color
    // alone.
    const inPickerMode = !!user && !selectedClinic && !selectedPatient && portalClinics.length > 1;

    if (!user || inPickerMode) {
      logClinicColors(`apply:fallback-healthcom (${!user ? 'logged-out' : 'picker-mode'})`);
      applyClinicColors(HEALTHCOM_PRIMARY, HEALTHCOM_ACCENT);
      persistClinicColors(undefined);
    }
  }, [authChecked, user, selectedPatient, selectedClinic, clinicLocationData, portalClinics]);

  const logout = async () => {
    await signOut();
    sessionStorage.removeItem('selectedPatientId');
    // Drop cached per-user data so the next login doesn't inherit the
    // previous user's clinic list, branding, or selected clinic.
    queryClient.removeQueries(ME_CLINICS_QUERY_KEY);
    persistClinicColors(undefined);
    persistSelectedClinicId(undefined);
    setUser(null);
    setMe(null);
    setValues(scheduleFormDefaults);
    setPortalClinics([]);
    setSelectedClinic(null);
    setPortalPatients([]);
    setSelectedPatient(null);
    navigate('/');
    // Tell RootLanding to flip authStep back to 'login' so Container
    // is unmounted. Without this, RootLanding keeps rendering
    // Container with user=null, which falls through to the public
    // Home view (Book/Call hero) until the user refreshes.
    onSignedOut?.();
  };

  // Survey-email subscription toggle (matches old UserNavigation in
  // healthcom-app). Flips `Patient.unsubscribe`, which gates the
  // 30-day post-care survey emails sent by chiroup-backend-api's
  // `sendThirtyDaySurvey` cron. Backend route verifies the body's
  // patientId belongs to the active session via getOwnedPatientIds.
  const toggleSurveyEmails = useCallback(async () => {
    if (!selectedPatient) return;

    const nextValue: 0 | 1 = selectedPatient.unsubscribe ? 0 : 1;

    try {
      await healthcomService.surveyEmails(nextValue, selectedPatient.ID);
    } catch (err) {
      console.error('surveyEmails toggle failed', err);

      return;
    }

    setSelectedPatient(prev => (prev ? { ...prev, unsubscribe: nextValue } : prev));
    setPortalPatients(prev =>
      prev.map(p => (p.ID === selectedPatient.ID ? { ...p, unsubscribe: nextValue } : p))
    );
  }, [selectedPatient]);

  const handleSelectClinic = useCallback(
    async (clinic: PatientClinic) => {
      // Switch clinics via localStorage + clinicId — no URL change.
      // Rebinds the existing session to the target clinic FIRST (so
      // downstream calls like mePortal pass the clinicId === auth.clinicId
      // guard), then clears the prior patient state, persists the new
      // clinicId for refresh survival, and refetches portal data for
      // the new clinic. URL path stays wherever it was.
      try {
        await authService.switchClinic({ clinicId: clinic.clinicId });
      } catch (err: any) {
        // Patient portal — log status + message only, not the full
        // normalized error (which may contain response payloads).
        console.error('Failed to switch clinic:', err?.response?.status, err?.message);

        return;
      }

      sessionStorage.removeItem('selectedPatientId');
      persistSelectedClinicId(clinic.clinicId);
      setSelectedPatient(null);
      setPortalPatients([]);
      setSelectedClinic(clinic);
      // Any in-progress booking belonged to the previous clinic's
      // patient context and isn't valid here.
      setValues(scheduleFormDefaults);
      fetchPortalData(clinic.clinicId);
    },
    [fetchPortalData]
  );

  const handleSelectPatient = useCallback((patient: PatientInfo) => {
    setSelectedPatient(patient);
    sessionStorage.setItem('selectedPatientId', patient.ID);
    // Wipe any in-progress booking — it was for the previous patient.
    setValues(scheduleFormDefaults);

    if (patient.language) {
      setLanguage(patient.language as Language);
    }
  }, []);

  const resetClinicSelection = useCallback(() => {
    // Clear only the selection — don't touch selectedPatient. If the user
    // picks the same clinic back from the switcher, they should land on
    // their previous patient. If they pick a different clinic,
    // handleSelectClinic will reset the patient state as part of the
    // navigation.
    setSelectedClinic(null);
  }, []);

  const resetPatientSelection = useCallback(() => {
    setSelectedPatient(null);
    sessionStorage.removeItem('selectedPatientId');
    // The booking under construction was tied to the patient being
    // unselected — clear it so the next patient starts fresh.
    setValues(scheduleFormDefaults);
  }, []);

  const portalContextValue = useMemo(
    () => ({
      clinics: portalClinics,
      selectedClinic,
      patients: portalPatients,
      selectedPatient,
      isLoading: portalLoading,
      language,
      selectClinic: handleSelectClinic,
      selectPatient: handleSelectPatient,
      resetClinicSelection,
      resetPatientSelection,
      // In-page refresh: keep the current page mounted (no skeleton
      // swap) so post-action UI like the payment confirmation survives.
      refreshPatientData: () => fetchPortalData(undefined, { silent: true }),
    }),
    [
      portalClinics,
      selectedClinic,
      portalPatients,
      selectedPatient,
      portalLoading,
      language,
      handleSelectClinic,
      handleSelectPatient,
      resetClinicSelection,
      resetPatientSelection,
      fetchPortalData,
    ]
  );

  if (downForMaintenance) {
    return <Maintenance title={downForMaintenance.title} message={downForMaintenance.message} />;
  }

  // Note: while `loaderPhase === 'visible'` the app-level
  // LoadingOverlay covers the viewport with the loader. We still
  // render the portal beneath it — that's intentional, it lets the
  // overlay's fade-out reveal a populated portal with no flash.
  // The `hc-content-fade-in` class below pairs the portal's
  // entrance with the overlay's exit timing.

  // Authenticated but portal data is still in flight. Previously this
  // returned a full-viewport gradient — now we let Container render
  // its chrome (header + sidebar) and slot a `MainContentSkeleton`
  // into the main column so the user sees the layout immediately and
  // watches content fill in, instead of a hard cut from gradient to
  // app. The chrome falls back to HealthCom defaults (logo, no patient
  // name in the user menu) until selectedPatient lands.

  // NOTE: deliberately NO `!clinicLocationData` / `!disciplines?.length`
  // guards here. Those were relics of the old path-based routing where
  // `useLocation()` always fetched `/api/location/{path}`. With the
  // authed portal now keyed by clinicId (no URL path), those guards
  // would fire on every mount and block the user from the app.
  //
  // Schedule-specific "not setup" messaging belongs inside the Schedule
  // route itself — the portal (Home, HealthHub, Exercises, etc.) works
  // fine without scheduling being configured.

  // The authed portal is path-less — BottomNav, Home, HealthHub etc.
  // build URLs off `basePath` and we want them to stay at `/`, `/health`,
  // `/schedule`, etc. The clinic's vanity path is used only for backend
  // lookups (useLocation, legacy Schedule/Login props), never for
  // navigation. Don't conflate the two.
  const basePath = '';

  // Child components that still thread the clinic path through to the
  // backend (Schedule availability lookup, legacy Login flow,
  // Appointments cancel) read it from this string. Coalesced to '' so
  // TS prop types stay happy even before a clinic is picked.
  const pathForLegacyProps = path || '';

  // Determine if we need clinic or patient selection (only when authenticated)
  const needsClinicSelection =
    user && !portalLoading && portalClinics.length > 1 && !selectedClinic;
  const needsPatientSelection =
    user && !portalLoading && selectedClinic && portalPatients.length > 1 && !selectedPatient;

  // Show bottom nav when user is authenticated and has selected a patient
  const showBottomNav = user && selectedPatient;

  // The full authed shell (sidebar at lg+, max-w-7xl two-column layout)
  // is visible whenever the user is signed in AND we're past the
  // pre-portal selection gates. That includes the brief portalLoading
  // window after refresh — keeping the shell up means the SideNav
  // doesn't pop in once /me/{path} resolves, and MainContentSkeleton
  // sits in its actual final column instead of a narrow centered one.
  const showAuthedShell = user && !needsClinicSelection && !needsPatientSelection;

  return (
    <PatientPortalContext.Provider value={portalContextValue}>
      <ScrollToTop />
      <div
        className={cn(
          'min-h-screen',
          showAuthedShell && 'pb-24 lg:pb-0',
          // Gentle fade + 1% scale-in for the portal so it settles
          // into place underneath the dissolving loader instead of
          // popping in. Only applies to the very first paint after
          // the loader has finished its work.
          loaderPhase === 'leaving' && 'hc-content-fade-in'
        )}
      >
        <header className="sticky top-0 z-50 bg-white/80 dark:bg-[var(--color-chrome-bg)] backdrop-blur-lg border-b border-[var(--color-chrome-border)]">
          {/* Constrain the header's inner row to the same max-width as
              the sidebar+content column below, so the logo lines up
              with the sidebar's left edge and the user menu lines up
              with the main column's right edge on desktop. */}
          <nav
            className="mx-auto max-w-lg md:max-w-3xl lg:max-w-7xl flex items-center justify-between p-4 lg:px-8"
            aria-label="Global"
          >
            <div className="flex items-center gap-3 lg:flex-1">
              <Link to="/" className="-m-1.5 p-1.5">
                {(() => {
                  // Prefer the enriched clinic from mePortal
                  // (selectedPatient.clinic), fall back to the lighter
                  // summary from meClinics (selectedClinic), then the
                  // HealthCom brand when no clinic is active.
                  const activeClinic = selectedPatient?.clinic || selectedClinic;
                  const showHealthcom = needsClinicSelection || !activeClinic?.logo;
                  const logoSrc = showHealthcom ? healthcomLogo : activeClinic?.logo;
                  const logoAlt = showHealthcom
                    ? 'HealthCom'
                    : (activeClinic as any)?.name || (activeClinic as any)?.clinicName || 'Clinic';

                  return (
                    <>
                      <span className="sr-only">{logoAlt}</span>
                      <img className="h-12 w-auto" src={logoSrc} alt={logoAlt} />
                    </>
                  );
                })()}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleTheme}
                aria-label={
                  themeResolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
                }
                className="flex size-9 items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-white/5"
              >
                {themeResolved === 'dark' ? (
                  <SunIcon className="size-5" />
                ) : (
                  <MoonIcon className="size-5" />
                )}
              </button>
              {user ? (
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="flex items-center text-gray-700 hover:text-gray-900 dark:text-zinc-300 dark:hover:text-zinc-100">
                      <span className="sr-only">Open options</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </Menu.Button>
                  </div>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-zinc-800 shadow-lg dark:shadow-none ring-1 ring-black/5 dark:ring-white/10 focus:outline-none">
                      <div className="py-1">
                        {selectedPatient && (
                          <div className="px-4 py-2 border-b border-gray-100 dark:border-white/10">
                            <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                              {selectedPatient.fname} {selectedPatient.lname}
                            </p>
                            {selectedPatient.clinic?.name && (
                              <p className="text-xs text-gray-500 dark:text-zinc-400">
                                {selectedPatient.clinic.name}
                              </p>
                            )}
                          </div>
                        )}
                        {portalClinics.length > 1 && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={resetClinicSelection}
                                className={cn(
                                  active
                                    ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                    : 'text-gray-700 dark:text-zinc-300',
                                  'block w-full px-4 py-2 text-left text-sm'
                                )}
                              >
                                Switch Clinic
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {portalPatients.length > 1 && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={resetPatientSelection}
                                className={cn(
                                  active
                                    ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                    : 'text-gray-700 dark:text-zinc-300',
                                  'block w-full px-4 py-2 text-left text-sm'
                                )}
                              >
                                Switch Patient
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        <div className="my-1 border-t border-gray-100 dark:border-white/10" />
                        {/* Static help-center links — same destinations
                            the old healthcom-app's UserNavigation used.
                            Help-center pages cover both Terms of Use
                            and Privacy Policy; opening in a new tab
                            keeps the patient's session intact. */}
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="https://help.chiroup.com/en_US/security-privacy/healthcomio-terms-of-use"
                              target="_blank"
                              rel="noreferrer"
                              className={cn(
                                active
                                  ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                  : 'text-gray-700 dark:text-zinc-300',
                                'block w-full px-4 py-2 text-left text-sm'
                              )}
                            >
                              {hcVerbiage.termsOfUse[language]}
                            </a>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <a
                              href="https://help.chiroup.com/en_US/security-privacy/healthcomio-privacy-policy"
                              target="_blank"
                              rel="noreferrer"
                              className={cn(
                                active
                                  ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                  : 'text-gray-700 dark:text-zinc-300',
                                'block w-full px-4 py-2 text-left text-sm'
                              )}
                            >
                              {hcVerbiage.privacyPolicy[language]}
                            </a>
                          )}
                        </Menu.Item>
                        {selectedPatient && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={toggleSurveyEmails}
                                className={cn(
                                  active
                                    ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                    : 'text-gray-700 dark:text-zinc-300',
                                  'block w-full px-4 py-2 text-left text-sm'
                                )}
                              >
                                {
                                  hcVerbiage[
                                    selectedPatient.unsubscribe ? 'resubscribe' : 'unsubscribe'
                                  ][language]
                                }
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        <div className="my-1 border-t border-gray-100 dark:border-white/10" />
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={logout}
                              className={cn(
                                active
                                  ? 'bg-gray-100 text-gray-900 dark:bg-zinc-700 dark:text-zinc-100'
                                  : 'text-gray-700 dark:text-zinc-300',
                                'block w-full px-4 py-2 text-left text-sm'
                              )}
                            >
                              Sign Out
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              ) : (
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    (isActive ? 'invisible ' : '') +
                    'lg:flex lg:flex-1 text-sm font-semibold leading-6 text-gray-900 dark:text-gray-100 cursor-pointer'
                  }
                  end
                >
                  Log in <span aria-hidden="true">&rarr;</span>
                </NavLink>
              )}
            </div>
          </nav>
        </header>
        {/* Layout shell:
            - mobile / tablet portrait: single centered column, BottomNav handles primary navigation.
            - lg+ (iPad Pro, iPad Air landscape, desktop): the body is
              a flex row with a sticky SideNav on the left and main
              content on the right, both sharing one max-w-7xl column.
              Pre-clinic states (clinic picker, patient picker) keep
              the narrow centered look so there's no oversized empty
              rail before the user has somewhere to navigate. */}
        <div
          className={cn(
            'mx-auto px-4 sm:px-6 lg:px-8',
            showAuthedShell ? 'max-w-lg md:max-w-3xl lg:max-w-7xl lg:flex lg:gap-8' : 'max-w-lg'
          )}
        >
          {showAuthedShell && (
            <aside className="hidden lg:block lg:w-56 lg:shrink-0 lg:pt-8">
              <SideNav
                basePath={basePath}
                language={language}
                hasEhr={!!selectedPatient?.clinic?.ehr}
                allowOnlinePayments={!!selectedPatient?.clinic?.allowOnlinePayments}
              />
            </aside>
          )}
          <main className="isolate flex-1 min-w-0">
            <div className="relative">
              <div className="py-5 sm:py-8">
                {/* Clinic selection gate */}
                {needsClinicSelection && (
                  <ClinicSelect
                    clinics={portalClinics}
                    onSelect={handleSelectClinic}
                    language={language}
                  />
                )}

                {/* Patient selection gate */}
                {needsPatientSelection && (
                  <PatientSelect
                    patients={portalPatients}
                    onSelect={handleSelectPatient}
                    language={language}
                  />
                )}

                {/* Portal loading */}
                {user && portalLoading && <MainContentSkeleton />}

                {/* Main content - only show when selections are complete or not needed */}
                {(!user || !portalLoading) && !needsClinicSelection && !needsPatientSelection && (
                  <Routes>
                    {/* Home - dashboard for authenticated users, schedule for guests */}
                    {/*
                     * `/` is always Home in the authed portal. Previously
                     * this fell back to <Schedule> for guests, which made
                     * sense under the old path-based routing where
                     * anyone could land on `/:clinic-path` without being
                     * signed in. The guest booking flow is moving to its
                     * own entry point; keep this route focused on the
                     * authed patient portal so an unpopulated
                     * selectedPatient doesn't accidentally render the
                     * "Not Setup" message from Schedule's internal
                     * guards.
                     */}
                    <Route path="/" element={<Home basePath={basePath} />} />

                    {/* Schedule appointment */}
                    <Route
                      path="/schedule"
                      element={
                        <Schedule
                          clinicLocationData={clinicLocationData}
                          path={pathForLegacyProps}
                          user={user}
                          setUser={setUser}
                          getMe={getMe}
                          me={me}
                          values={values}
                          setValues={setValues}
                        />
                      }
                    />

                    {/* Login */}
                    <Route
                      path="/login"
                      element={
                        <Login
                          setUser={setUser}
                          path={pathForLegacyProps}
                          getMe={getMe}
                          values={values}
                          setValues={setValues}
                          me={me}
                          clinicLocationData={clinicLocationData}
                        />
                      }
                    />
                    <Route path="/thank-you" element={<ThankYou path={pathForLegacyProps} />} />

                    {/* Appointments */}
                    <Route
                      path="/appointments"
                      element={
                        <Appointments
                          path={pathForLegacyProps}
                          clinicId={activeClinicId}
                          location={clinicLocationData}
                          me={me}
                          primaryColor={clinicLocationData?.primaryColor || primaryColorDefault}
                        />
                      }
                    />

                    {/* Health hub */}
                    <Route path="/health" element={<HealthHub basePath={basePath} />} />

                    {/* Exercises */}
                    <Route path="/exercises" element={<Exercises basePath={basePath} />} />
                    <Route
                      path="/exercises/:exerciseId"
                      element={<ExerciseDetail basePath={basePath} />}
                    />

                    {/* Conditions */}
                    <Route path="/conditions" element={<Conditions basePath={basePath} />} />
                    <Route
                      path="/conditions/:conditionId"
                      element={<ConditionDetail basePath={basePath} />}
                    />

                    {/* Advice */}
                    <Route path="/advice" element={<Advices basePath={basePath} />} />
                    <Route
                      path="/advice/:adviceId"
                      element={<AdviceDetail basePath={basePath} />}
                    />

                    {/* Per-appointment surveys (slim runner) */}
                    <Route
                      path="/surveys/:patientSurveyId"
                      element={<SurveyRunner basePath={basePath} />}
                    />

                    {/* Per-appointment consent runner */}
                    <Route
                      path="/consents/:patientConsentId"
                      element={<ConsentRunner basePath={basePath} />}
                    />

                    {/* Per-appointment upload runners — Photo ID and
                        Insurance Card share the same UI. */}
                    <Route
                      path="/uploads/photoId/:requestId"
                      element={<UploadRunner kind="photoId" basePath={basePath} />}
                    />
                    <Route
                      path="/uploads/insuranceCard/:requestId"
                      element={<UploadRunner kind="insuranceCard" basePath={basePath} />}
                    />

                    {/* Per-request form runners — basic info + insurance.
                        Each loads the request via Cognito then submits
                        through the legacy validation-code-authed
                        endpoints used by the public request flow. */}
                    <Route
                      path="/forms/basicInformation/:requestId"
                      element={<RequestFormRunner kind="basicInformation" basePath={basePath} />}
                    />
                    <Route
                      path="/forms/insurance/:requestId"
                      element={<RequestFormRunner kind="insurance" basePath={basePath} />}
                    />

                    {/* Clinic info */}
                    <Route path="/clinic" element={<ClinicInfo />} />

                    {/* Balance & Payments — gated on `hasEHR` +
                        `allowOnlinePayments` (off by default). When the
                        clinic hasn't enabled online payments the tab is
                        hidden and this route redirects home, so the
                        "online payments unavailable" notice never shows. */}
                    <Route
                      path="/balance"
                      element={
                        selectedPatient?.clinic?.allowOnlinePayments ? (
                          <BalancePlaceholder />
                        ) : (
                          <Navigate to={basePath || '/'} replace />
                        )
                      }
                    />

                    <Route path="*" element={<NotFoundSchedule />} />
                  </Routes>
                )}
              </div>
            </div>
          </main>
        </div>
        {showAuthedShell && (
          <BottomNav
            basePath={basePath}
            language={language}
            hasEhr={!!selectedPatient?.clinic?.ehr}
            allowOnlinePayments={!!selectedPatient?.clinic?.allowOnlinePayments}
          />
        )}
      </div>
    </PatientPortalContext.Provider>
  );
};

export default Container;
